<script lang="ts">
    import * as Dialog from "$lib/components/ui/dialog/index.js";
    import Button from "@/lib/components/ui/button/button.svelte";
    import Input from "@/lib/components/ui/input/input.svelte";
    import Label from "@/lib/components/ui/label/label.svelte";
    import { round } from "@/Util/utils";

    /**
     * Adjusting the fitness signature on the account setup wizard's Plan your Training step: Threshold and Peak Power as
     * the user knows them, and the High Intensity Energy that goes with them when there is nothing else to go on (28 J for
     * each watt between them, as for a setup without data). onSave sets them as the starting signature.
     */
    export let open = false
    export let ftp: number
    export let pp: number
    export let onSave: (ftp: number, pp: number) => Promise<void>

    let ftpInput = ""
    let ppInput = ""
    let saving = false
    let error = ""
    // Start from today's signature each time the dialog opens
    $: if (open) reset()
    function reset() {
        if (saving) return
        ftpInput = String(Math.round(ftp))
        ppInput = String(Math.round(pp))
        error = ""
    }

    // Not below the floors a new user's signature is held to (the wizard's p0: 50 and 100 W, see postWizardSignature), and HIE
    // over 1000 J, or the signature isn't used (User::getSignature): Peak Power at least 36 W over Threshold
    const MIN_FTP = 50
    const MIN_PP = 100
    const MIN_PP_OVER_TP = Math.ceil(1000/28 + 1e-9)
    $: newFtp = Number(ftpInput)
    $: newPp = Number(ppInput)
    $: valid = newFtp >= MIN_FTP && newFtp <= 800 && newPp >= Math.max(MIN_PP, newFtp + MIN_PP_OVER_TP) && newPp <= 3000
    $: hie = valid ? 28*(newPp - newFtp) : null

    async function save() {
        if (!valid || saving) return
        saving = true
        error = ""
        try {
            await onSave(newFtp, newPp)
            open = false
        } catch (e: any) {
            error = e?.message || "Your fitness signature could not be saved. Please try again."
        } finally {
            saving = false
        }
    }
</script>

<Dialog.Root bind:open portal="#shadcn-ui-dialog-target" closeOnOutsideClick={!saving} closeOnEscape={!saving}>
    <Dialog.Content class="max-w-sm" overlayClass="bg-black/50 backdrop-blur-none">
        <Dialog.Header>
            <Dialog.Title>Adjust your fitness signature</Dialog.Title>
            <Dialog.Description>
                Set your Threshold Power and Peak Power if you know them. Your High Intensity Energy is estimated from them.
            </Dialog.Description>
        </Dialog.Header>
        <form class="grid grid-cols-[1fr,7rem] items-center gap-x-4 gap-y-3 mt-2" on:submit|preventDefault={save}>
            <Label class="mb-0" for="adjust-ftp">Threshold Power (TP)</Label>
            <div class="flex items-center gap-1">
                <Input id="adjust-ftp" type="number" inputmode="numeric" min={MIN_FTP} max="800" step="1" bind:value={ftpInput} disabled={saving}/>
                <span class="text-slate-500 text-sm">w</span>
            </div>
            <Label class="mb-0" for="adjust-pp">Peak Power (PP)</Label>
            <div class="flex items-center gap-1">
                <Input id="adjust-pp" type="number" inputmode="numeric" min={MIN_PP} max="3000" step="1" bind:value={ppInput} disabled={saving}/>
                <span class="text-slate-500 text-sm">w</span>
            </div>
            <span class="text-slate-600 text-sm">High Intensity Energy (HIE)</span>
            <span class="font-semibold tabular-nums">{hie != null ? round(hie/1000, 1) : "–"}<span class="text-slate-500 text-sm font-normal ml-1">kJ</span></span>
            {#if !valid}
                <span class="col-span-2 text-sm text-red-700">Threshold Power is between {MIN_FTP} and 800 w, and Peak Power at least {MIN_PP} w and {MIN_PP_OVER_TP} w higher.</span>
            {/if}
            {#if error}
                <span class="col-span-2 text-sm text-red-700" role="alert">{error}</span>
            {/if}
            <Dialog.Footer class="col-span-2 mt-2 flex gap-2 sm:flex-row-reverse">
                <Button type="submit" disabled={!valid || saving}>{saving ? "Saving…" : "Save"}</Button>
                <Button type="button" variant="outline" disabled={saving} on:click={() => open = false}>Cancel</Button>
            </Dialog.Footer>
        </form>
    </Dialog.Content>
</Dialog.Root>
